import { AlertCircle, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { apiFetch } from "@/lib/api";

interface NotificationDefault {
  eventType: string;
  channel: string;
  enabled: boolean;
}

interface OnboardingStep2Props {
  jobTitle: string | null;
  phone: string | null;
  onComplete: () => void;
}

const EVENT_LABELS: Record<string, string> = {
  pay_app_submitted: "Pay Application Submitted",
  compliance_alert: "Compliance Alert",
  task_due: "Task Due",
  punch_list_item_assigned: "Punch List Item Assigned",
  change_order_approved: "Change Order Approved",
  user_invited: "User Invited",
};

const CHANNEL_LABELS: Record<string, string> = {
  email: "Email",
  in_app: "In-App",
  push: "Push",
};

export function OnboardingStep2({ jobTitle, phone, onComplete }: OnboardingStep2Props) {
  const [title, setTitle] = useState(jobTitle ?? "");
  const [phoneVal, setPhoneVal] = useState(phone ?? "");
  const [prefs, setPrefs] = useState<NotificationDefault[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiFetch<NotificationDefault[]>("/onboarding/notification-defaults")
      .then((defaults) => setPrefs(defaults))
      .catch(() => {});
  }, []);

  function togglePref(eventType: string, channel: string) {
    setPrefs((prev) =>
      prev.map((p) =>
        p.eventType === eventType && p.channel === channel ? { ...p, enabled: !p.enabled } : p,
      ),
    );
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await fetch("/api/onboarding/step/2", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          jobTitle: title || null,
          phone: phoneVal || null,
          notificationPreferences: prefs,
        }),
      });
      if (!res.ok) {
        const data: { error?: string } = await res.json().catch(() => ({}));
        setError(data.error ?? "Failed to save profile.");
        return;
      }
      toast.success("Profile saved");
      onComplete();
    } catch {
      setError("An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  }

  const grouped = prefs.reduce<Record<string, NotificationDefault[]>>((acc, p) => {
    const key = p.eventType;
    if (!acc[key]) acc[key] = [];
    acc[key].push(p);
    return acc;
  }, {});

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-1">
        <h2 className="text-xl font-semibold">Complete your profile</h2>
        <p className="text-sm text-muted-foreground">
          Add some details and set up your notification preferences.
        </p>
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-lg bg-red-500/10 p-3 text-sm text-red-600 ring-1 ring-red-500/20 dark:text-red-400">
          <AlertCircle className="mt-0.5 size-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="ob-title">Job title</Label>
          <Input
            id="ob-title"
            placeholder="e.g. Project Manager"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="ob-phone">Phone number</Label>
          <Input
            id="ob-phone"
            type="tel"
            placeholder="e.g. (617) 555-0100"
            value={phoneVal}
            onChange={(e) => setPhoneVal(e.target.value)}
          />
        </div>
      </div>

      {prefs.length > 0 && (
        <div className="space-y-3">
          <p className="text-sm font-medium">Notification preferences</p>
          <div className="space-y-3 rounded-lg border p-4">
            {Object.entries(grouped).map(([eventType, rows]) => (
              <div key={eventType} className="space-y-2">
                <p className="text-sm font-medium">{EVENT_LABELS[eventType] ?? eventType}</p>
                <div className="flex flex-wrap gap-4">
                  {rows.map((p) => {
                    const switchId = `notif-${p.eventType}-${p.channel}`;
                    return (
                      <div
                        key={`${p.eventType}-${p.channel}`}
                        className="flex items-center gap-2 text-sm text-muted-foreground"
                      >
                        <Switch
                          id={switchId}
                          checked={p.enabled}
                          onCheckedChange={() => togglePref(p.eventType, p.channel)}
                        />
                        <Label
                          htmlFor={switchId}
                          className="cursor-pointer font-normal text-muted-foreground"
                        >
                          {CHANNEL_LABELS[p.channel] ?? p.channel}
                        </Label>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Button type="submit" disabled={loading} className="w-full">
        {loading ? <Loader2 className="mr-2 size-4 animate-spin" /> : null}
        Finish setup
      </Button>
    </form>
  );
}
